import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { normalizeText } from '../../utils/dataUtils';
import { calculateSizeTag } from '../../utils/dogUtils';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const AVATAR_COLORS = ['dog', 'dog2', 'dog3', 'dog4', 'dog5'];
const avatarColor = (id) => AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];

const SIZE_LABEL = { S: 'Pequeño', M: 'Mediano', L: 'Grande', XL: 'Extra grande' };

export default function DogPickerSheet({ onClose, onPick, selectedDogs = [] }) {
  const { authorizedFetch } = useAuth();
  const [query, setQuery] = useState('');
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() =>
    selectedDogs.map(d => d._id || d.id).filter(Boolean)
  );

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authorizedFetch(`${API_URL}/api/dogs?limit=1000`);
        if (res.ok) {
          const data = await res.json();
          const dogsList = data.data ? data.data : (Array.isArray(data) ? data : []);
          setDogs(dogsList);
        }
      } catch {
        setDogs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authorizedFetch]);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  const q = normalizeText(query);
  const filtered = dogs.filter(d => {
    if (!q) return true;
    const ownerName = d.owners?.[0]?.name || '';
    return normalizeText(`${d.name} ${d.breed} ${ownerName}`).includes(q);
  });

  const sizeOf = (dog) => {
    const tag = calculateSizeTag(dog.weight).tag;
    return tag === '?' ? 'M' : tag;
  };

  const toggle = (dog) => {
    const id = dog._id || dog.id;
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleConfirm = () => {
    const picked = dogs
      .filter(d => selected.includes(d._id || d.id))
      .map(d => ({ ...d, _resolvedSize: sizeOf(d) }));
    onPick(picked);
  };

  return (
    <div className="pos-backdrop" onClick={onClose}>
      <div className="pos-sheet" style={{ height: '76dvh' }} onClick={e => e.stopPropagation()}>
        <div className="pos-sheet-grabber" />
        <div className="pos-sheet-head">
          <h3>Seleccionar perro{selected.length > 0 ? ` (${selected.length})` : ''}</h3>
          <button className="pos-sheet-close" onClick={onClose}>✕</button>
        </div>

        <div className="pos-dog-search" style={{ flexShrink: 0 }}>
          <span className="pos-dog-search-ic">🔍</span>
          <input
            autoFocus
            placeholder="Buscar perro, raza, dueño…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>

        <div className="pos-dog-list">
          {loading && <div className="pos-hint">Cargando perros…</div>}
          {!loading && filtered.map(dog => {
            const size = sizeOf(dog);
            const owner = dog.owners?.[0];
            const isSelected = selected.includes(dog._id || dog.id);
            return (
              <div
                key={dog._id}
                className={`pos-dog-card${isSelected ? ' pos-dog-card--selected' : ''}`}
                onClick={() => toggle(dog)}
              >
                <div className={`pos-customer-avatar ${avatarColor(dog._id?.charCodeAt?.(0))}`}>🐕</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="pos-dog-name">{dog.name}</div>
                  <div className="pos-dog-meta">
                    {dog.breed}{owner ? ` · ${owner.name}` : ''}
                  </div>
                </div>
                <span className="pos-size-pill">{SIZE_LABEL[size] || size}</span>
                {isSelected && <span className="pos-dog-check">✓</span>}
              </div>
            );
          })}
          {!loading && filtered.length === 0 && (
            <div className="pos-hint">Sin resultados.</div>
          )}
        </div>

        <div className="pos-sheet-footer">
          <button
            className="pos-cta-btn"
            style={{ margin: '0 16px 16px', borderRadius: 14 }}
            disabled={selected.length === 0}
            onClick={handleConfirm}
          >
            {selected.length === 0 ? 'Selecciona un perro' : `Confirmar ${selected.length} perro${selected.length > 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
